import React, { useEffect, useState } from 'react';
import { Box, Typography, Link } from '@mui/material';
import client from '../../../utils/contentfulClient';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const CARD_COUNT = 4;

const formatBlogDate = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

const mapPostToCard = (post) => {
  const { slug, category, heading, blurb, blogDate, headingImage } =
    post.fields;
  const cleanSlug = slug.replace(/\/$/, '');
  const link = cleanSlug.includes('/')
    ? `/blog/${slug}`
    : `/blog/${category}/${slug}`;

  return {
    image: headingImage?.fields?.file?.url
      ? `https:${headingImage.fields.file.url}`
      : '',
    date: formatBlogDate(blogDate),
    title: heading,
    body: blurb,
    link,
  };
};

const FourBlogCardsMobileComponent = ({ data, tag }) => {
  const { title, subtitle, description, footer } = data;
  const resolvedSubtitle = subtitle || description || '';
  const [cards, setCards] = useState([]);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const taggedResponse = await client.getEntries({
          content_type: 'blogPost',
          'fields.tags[in]': tag,
          order: '-fields.blogDate',
          limit: CARD_COUNT,
        });

        const taggedPosts = taggedResponse.items;
        let fetched = taggedPosts.map(mapPostToCard);

        if (fetched.length < CARD_COUNT) {
          const needed = CARD_COUNT - fetched.length;
          const excludeIds = new Set(taggedPosts.map((p) => p.sys.id));
          const fallbackResponse = await client.getEntries({
            content_type: 'blogPost',
            'fields.category': 'insurance',
            order: '-fields.blogDate',
            limit: 10,
          });

          const fallbackCards = fallbackResponse.items
            .filter((p) => !excludeIds.has(p.sys.id))
            .slice(0, needed)
            .map(mapPostToCard);

          fetched = [...fetched, ...fallbackCards];
        }

        setCards(fetched);
      } catch (err) {
        // ignore
      }
    };

    fetchCards();
  }, [tag]);

  return (
    <Box
      sx={{
        width: '100%',
        px: '16px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '40px',
      }}
    >
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <ScrollFadeWrapper variant="text">
          <Typography
            component={'h3'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '2rem',
              lineHeight: '2.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mb: '16px',
            }}
          >
            {title}
          </Typography>
        </ScrollFadeWrapper>

        <ScrollFadeWrapper variant="text" delay={0.2}>
          <Typography
            component={'p'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.25rem',
              lineHeight: '1.5rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
            }}
          >
            {resolvedSubtitle}
          </Typography>
        </ScrollFadeWrapper>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        {cards.map((card, index) => (
          <ScrollFadeWrapper key={index} delay={index * 0.1}>
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: '32px',
                boxShadow:
                  '0px 1px 2px 0px rgba(162, 172, 177, 0.2), 0px 12px 24px -4px rgba(22, 47, 59, 0.12)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Image
                width={358}
                height={220}
                src={card.image}
                alt={card.title}
                sx={{
                  width: '100%',
                  height: '220px',
                  objectFit: 'cover',
                  backgroundColor: '#A2ACB1',
                }}
              />

              <Box
                sx={{
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flexGrow: 1,
                  gap: '16px',
                }}
              >
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
                >
                  <Typography
                    component={'p'}
                    sx={{
                      fontFamily: 'Overline, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      lineHeight: '1.25rem',
                      textAlign: 'right',
                      color: '#162F3B',
                    }}
                  >
                    {card.date}
                  </Typography>

                  <Typography
                    component={'h6'}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.03em',
                      color: '#162F3B',
                      textAlign: 'left',
                    }}
                  >
                    {card.title}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Link
                    component={'a'}
                    href={card.link}
                    underline="none"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.02em',
                      color: '#162F3B',
                      '&:hover': {
                        color: '#9EA900',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Read More
                  </Link>
                </Box>
              </Box>
            </Box>
          </ScrollFadeWrapper>
        ))}
      </Box>

      <Typography
        component={'p'}
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: '1.5rem',
          letterSpacing: '-0.02em',
          color: '#162F3B',
          textAlign: 'center',
        }}
      >
        {footer}
      </Typography>
    </Box>
  );
};

export default FourBlogCardsMobileComponent;
